import React, { useState, useMemo } from "react";
import { Box, Menu, MenuItem } from "@mui/material";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import AppButton from "../AppButton";
import RfiReportManager from "./RfiReportManager";
import { generatePDFAdvanced } from "../../utils/pdfGenerator";
import toast from "react-hot-toast";

/**
 * ReportDownloadManager - Manages all report download functionality
 *
 * This component handles:
 * - Report dropdown menu with completed stages
 * - PDF generation logic for all stages
 * - Test report preview button
 * - Integration with RfiReportManager for template rendering
 *
 * @param {Object} props
 * @param {Array} props.stagesDetails - Array of stage details
 * @param {Object} props.rfiDetails - RFI details object
 */
const ReportDownloadManager = ({ stagesDetails, rfiDetails }) => {
  // State management
  const [reportAnchorEl, setReportAnchorEl] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [renderHiddenTemplate, setRenderHiddenTemplate] = useState(false);
  const [currentStageForPDF, setCurrentStageForPDF] = useState(null);

  const isReportMenuOpen = Boolean(reportAnchorEl);

  // Filter completed stages for reports dropdown (exclude skipped stages)
  const completedStages = useMemo(() => {
    if (!stagesDetails || !Array.isArray(stagesDetails)) return [];
    return stagesDetails.filter(
      (stage) => stage.status === "Completed" && !stage.is_skipped
    );
  }, [stagesDetails]);

  // Handlers
  const handleReportMenuClick = (event) => {
    setReportAnchorEl(event.currentTarget);
  };

  const handleReportMenuClose = () => {
    setReportAnchorEl(null);
  };

  /**
   * Handle report download for a specific stage
   * @param {number} stageNum - Stage number (1-7)
   */
  const handleReportDownload = async (stageNum) => {
    console.log("📥 Starting report download for stage:", stageNum);
    handleReportMenuClose(); // Close dropdown immediately
    setIsGenerating(true);
    try {
      console.log("✅ Using new template system for stage", stageNum);
      setCurrentStageForPDF(stageNum); // Track which stage to render
      setRenderHiddenTemplate(true); // Render hidden template

      // Map stage numbers to report names
      const stageNames = {
        1: "components",
        2: "assembly",
        3: "blasting",
        4: "primer-coat",
        5: "second-coat",
        6: "top-coat",
        7: "additions",
      };

      setTimeout(async () => {
        console.log(
          "🎨 Generating PDF for template:",
          `stage-${stageNum}-report-template`
        );
        await generatePDFAdvanced(
          `stage-${stageNum}-report-template`,
          `${stageNames[stageNum]}-stage-${stageNum}-report.pdf`
        );
        console.log("✅ PDF generated successfully");
        setIsGenerating(false);
        setRenderHiddenTemplate(false); // Clean up hidden template
        setCurrentStageForPDF(null);
      }, 1000);
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Failed to generate PDF. Please try again.");
      setIsGenerating(false);
      setRenderHiddenTemplate(false); // Clean up on error
      setCurrentStageForPDF(null);
    }
  };

  return (
    <>
      {/* Report Download Dropdown Button */}
      {completedStages.length > 0 && (
        <Box>
          <AppButton
            variant="contained"
            buttonType="primary"
            size="small"
            startIcon={<CloudDownloadIcon />}
            endIcon={<KeyboardArrowDownIcon />}
            label={isGenerating ? "Generating..." : "Download Report"}
            onClick={handleReportMenuClick}
            disabled={isGenerating}
            sx={{
              minWidth: "auto",
            }}
          />
          <Menu
            anchorEl={reportAnchorEl}
            open={isReportMenuOpen}
            onClose={handleReportMenuClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            {completedStages.length > 0 ? (
              completedStages.map((stage) => {
                // Extract stage number from stage_number (e.g., "Stage_3" -> 3 or just use the number)
                const stageNum =
                  typeof stage.stage_number === "string"
                    ? parseInt(
                        stage.stage_number.split("_")[1] || stage.stage_number
                      )
                    : stage.stage_number;

                return (
                  <MenuItem
                    key={stage.stage_id}
                    onClick={() => handleReportDownload(stageNum)}
                    disabled={isGenerating}
                  >
                    Stage {stageNum}: {stage.stage_name}
                  </MenuItem>
                );
              })
            ) : (
              <MenuItem disabled>No completed stages available</MenuItem>
            )}
          </Menu>
        </Box>
      )}

      {/* RFI Report Manager - Handles all PDF templates and preview modal */}
      <RfiReportManager
        rfiDetails={rfiDetails}
        renderHiddenTemplate={renderHiddenTemplate}
        currentStageForPDF={currentStageForPDF}
      />
    </>
  );
};

export default ReportDownloadManager;
